import type { MechanicDefinition } from "./types";

const PHB_RACES: Record<string, { traits: string[]; darkvision: boolean; resistances: string[] }> = {
  human: { traits: ["Versatile (+1 all stats)", "Extra skill proficiency", "Extra feat at L1"], darkvision: false, resistances: [] },
  elf: { traits: ["Trance (4h replaces 8h sleep)", "Fey Ancestry (advantage vs charm)", "Perception proficiency", "Keen Senses"], darkvision: true, resistances: [] },
  dwarf: { traits: ["Stonecunning", "Dwarven Resilience (advantage vs poison)", "Tool proficiency"], darkvision: true, resistances: ["poison"] },
  halfling: { traits: ["Lucky (reroll nat 1s)", "Brave (advantage vs frightened)", "Nimble (move through larger creatures)"], darkvision: false, resistances: [] },
  gnome: { traits: ["Gnome Cunning (advantage on INT/WIS/CHA saves vs magic)", "Artificer's Lore or Natural Illusionist"], darkvision: true, resistances: [] },
  "half-orc": { traits: ["Relentless Endurance (drop to 1 HP instead of 0, 1/long rest)", "Savage Attacks (extra crit die on melee)"], darkvision: true, resistances: [] },
  "half-elf": { traits: ["Fey Ancestry", "2 extra skill proficiencies", "+1 to two stats"], darkvision: true, resistances: [] },
  tiefling: { traits: ["Hellish Rebuke (1/long rest)", "Thaumaturgy cantrip", "Infernal Legacy"], darkvision: true, resistances: ["fire"] },
  dragonborn: { traits: ["Breath Weapon (2d6 AoE, scales with level)", "Draconic Ancestry (choose element)"], darkvision: false, resistances: [] },
};

const POPULAR_FEATS = [
  "Sentinel — Opportunity attacks reduce speed to 0. React-attack when ally is hit.",
  "Lucky — 3 luck points/long rest. Reroll any d20.",
  "War Caster — Advantage on CON saves for concentration. Cast spells as opportunity attacks.",
  "Sharpshooter — -5 attack / +10 damage on ranged. No disadvantage at long range. Ignore cover.",
  "Great Weapon Master — -5 attack / +10 damage on heavy weapons. Bonus attack on crit/kill.",
  "Polearm Master — Bonus attack with butt end. Opportunity attack when enemies enter reach.",
  "Tough — +2 HP per level.",
  "Alert — +5 initiative. Can't be surprised.",
  "Resilient — +1 to chosen stat. Gain proficiency in that stat's saving throw.",
  "Mobile — +10 speed. No opportunity attacks from targets you attacked.",
];

export const racialTraits: MechanicDefinition = {
  id: "racial_traits",
  disabled: true,
  name: "Race, Species & Feats",
  description: "Racial abilities, darkvision, resistances, and feat selection at ASI levels",
  icon: "Users",
  category: "systems",
  aiHints: {
    purpose: "Adds D&D racial traits (darkvision, resistances, special abilities) and feat selection at ASI levels.",
    whenToUse: "D&D-style games with fantasy races. Also works for sci-fi species or any world with distinct species abilities.",
    synergies: ["character_classes", "skill_checks", "combat"],
    conflicts: [],
    prerequisites: [],
    tags: ["races", "species", "feats", "traits", "darkvision", "d&d"],
    complexity: 2,
    exampleConfig: { races: "dnd_standard", feats_enabled: true },
    stateVariables: ["player_race", "racial_traits_list", "selected_feats"],
  },
  configSchema: [
    {
      key: "feats_enabled",
      label: "Feats System",
      type: "boolean",
      default: true,
      description: "Allow feats as alternatives to Ability Score Increases at levels 4/8/12/16/19",
    },
    {
      key: "asi_levels",
      label: "ASI/Feat Levels",
      type: "text",
      default: "4,8,12,16,19",
      description: "Comma-separated levels that grant ASI or feat choice",
    },
  ],
  systemPromptSection: (config) => {
    const feats = config.feats_enabled !== false;
    const asiLevels = (config.asi_levels || "4,8,12,16,19").split(",").map((s: string) => s.trim());

    const raceEntries = Object.entries(PHB_RACES)
      .map(([race, data]) => `  - ${race.charAt(0).toUpperCase() + race.slice(1)}: ${data.darkvision ? "Darkvision. " : ""}${data.resistances.length ? `Resistant: ${data.resistances.join(", ")}. ` : ""}Traits: ${data.traits.join(", ")}`)
      .join("\n");

    const featList = POPULAR_FEATS.map((f) => `  - ${f}`).join("\n");

    return `## RACE/SPECIES & FEATS (Hard Mechanic)
- Track player_race, racial_traits, darkvision, damage_resistances, damage_vulnerabilities in state.
- Use set_race during character creation to set racial features.
- ALWAYS apply racial traits mechanically (darkvision removes darkness penalties, resistances halve damage, etc.).

### AVAILABLE RACES
${raceEntries}

### DAMAGE RESISTANCES & VULNERABILITIES
- Before applying damage, check target's damage_resistances and damage_vulnerabilities.
- Resistant = halve damage (after all other modifiers). Vulnerable = double damage. Immune = 0 damage.
- Track damage_immunities for complete immunity.

${feats ? `### FEATS & ASI
- At levels ${asiLevels.join(", ")}: offer the player a choice: +2 to one ability score, +1 to two scores, OR a feat.
- Use grant_feat to apply. Popular feats:
${featList}` : "### ABILITY SCORE INCREASES\n- At levels " + asiLevels.join(", ") + ": +2 to one ability score or +1 to two."}`;
  },
  initializeState: () => ({
    player_race: "",
    racial_traits: [],
    darkvision: false,
    damage_resistances: [],
    damage_vulnerabilities: [],
    damage_immunities: [],
    feats: [],
  }),
  onEvent: (event, context) => {
    // React to level_up at ASI levels to prompt feat/ASI choice
    if (event.type === "level_up") {
      const newLevel = event.data.new_level || 1;
      const asiLevels = (context.config.asi_levels || "4,8,12,16,19").split(",").map((s: string) => parseInt(s.trim()));
      if (asiLevels.includes(newLevel)) {
        const featsEnabled = context.config.feats_enabled !== false;
        return {
          narrativeNotes: [
            featsEnabled
              ? `🎯 Level ${newLevel} — ASI/Feat choice! Offer: +2 to one stat, +1 to two stats, OR a feat.`
              : `🎯 Level ${newLevel} — Ability Score Increase! Offer: +2 to one stat or +1 to two stats.`,
          ],
        };
      }
    }
    return null;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "set_race",
        description: "Set the player's race/species during character creation. Applies racial traits, darkvision, and resistances.",
        parameters: {
          type: "object",
          properties: {
            race: { type: "string", description: "Race name (e.g. elf, dwarf, human, tiefling)" },
            traits: { type: "array", items: { type: "string" }, description: "Racial traits to apply" },
            darkvision: { type: "boolean", description: "Whether the race has darkvision" },
            resistances: { type: "array", items: { type: "string" }, description: "Damage types this race is resistant to" },
          },
          required: ["race", "traits"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "grant_feat",
        description: "Grant a feat or ASI to the player at an ASI level. Mutually exclusive with stat increase for that level.",
        parameters: {
          type: "object",
          properties: {
            feat_name: { type: "string", description: "Name of the feat (or 'ASI' for ability score increase)" },
            effect: { type: "string", description: "Mechanical effect of the feat" },
            stat_changes: { type: "object", description: "Stat changes if ASI (e.g. {STR: 2} or {DEX: 1, WIS: 1})" },
          },
          required: ["feat_name", "effect"],
        },
      },
    },
  ],
};
